import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import Scheduler from './Scheduler';
import { isNativeTimeKeeper } from 'Models/createTimeKeeper';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';

export default class Scheduler2 extends Scheduler {
  unpauseTicks = (
    bpm: number,
    countInMeasures: number,
    startMeasure: number,
    tickNoteType: NoteType,
    absoluteTiming: boolean = false,
  ): Promise<void> | void => {
    logger.debug(
      '[Scheduler2.unpauseTicks] called, isPaused=',
      this.timeKeeper.isPaused(),
      'handlingPauseOrUnpause=',
      this.handlingPauseOrUnpause,
      'isNative=',
      isNativeTimeKeeper(this.timeKeeper),
      'bpm=',
      bpm,
      'startMeasure=',
      startMeasure,
      'absoluteTiming=',
      absoluteTiming,
    );
    if (!this.timeKeeper.isPaused() || this.handlingPauseOrUnpause) {
      logger.debug(
        '[Scheduler2.unpauseTicks] BAILING OUT — isPaused=',
        this.timeKeeper.isPaused(),
        'handlingPauseOrUnpause=',
        this.handlingPauseOrUnpause,
      );
      return;
    }
    this.handlingPauseOrUnpause = true;

    const elapsedTicks = this.timeKeeper?.calculateElapsedTicks();
    const numberOfTicksPerMeasure = this.timeKeeper.getTicksPerMeasure();

    let lastCompleteMeasure = Math.floor(elapsedTicks / numberOfTicksPerMeasure);
    if (isNaN(lastCompleteMeasure) || lastCompleteMeasure < 0) {
      lastCompleteMeasure = this.countInMeasures;
    }
    const measureOffset = absoluteTiming ? startMeasure : this.lastPauseMeasure;
    logger.debug(
      '[Scheduler2.unpauseTicks] proceeding: measureOffset=',
      measureOffset,
      'lastPauseMeasure=',
      this.lastPauseMeasure,
      'startMeasure=',
      startMeasure,
    );

    // Reset lastInvocationTick on all interval functions so the gapCheck
    // doesn't block them after resume (stale ticks from the previous session).
    this.resetIntervalFns();

    if (isNativeTimeKeeper(this.timeKeeper)) {
      // Native path: startNativeScheduler owns the unpause + start sequence
      // because it must fetch native time before calling unpause().
      // Return the promise so callers can await native scheduler completion
      // before processing queued MIDI events (fixes first-note-always-miss).
      logger.debug(
        `[MUS119] Scheduler2.unpauseTicks NATIVE — measureOffset=${measureOffset} intervalFns=[${this.intervalFns
          .map((f) => `${f.name}:lastTick=${f.lastInvocationTick}`)
          .join(',')}]`,
      );
      const nativeTimedStart = this.takeTimedStart(bpm, measureOffset);
      return this.startNativeScheduler(
        bpm,
        nativeTimedStart?.countInMeasuresFromNow ?? 0,
        measureOffset,
        tickNoteType,
      )
        .then(() => {
          for (const unpauseCb of this.unpauseFns) {
            unpauseCb(
              this.timeKeeper?.calculateElapsedTicks(),
              this.timeKeeper?.getPlaybackStartTicks(),
              this.timeKeeper?.getTicksPerMeasure(),
            );
          }
          this.handlingPauseOrUnpause = false;
        })
        .catch((err: unknown) => {
          logger.error('[Scheduler2.unpauseTicks] native scheduler failed:', err);
          this.handlingPauseOrUnpause = false;
          awsRum?.recordError(err instanceof Error ? err : new Error(String(err)));
        });
    } else {
      const timedStart = this.takeTimedStart(bpm, measureOffset);
      // A timed start passes where the start measure plays; others start now.
      this.timeKeeper.unpause(
        bpm,
        0,
        measureOffset,
        tickNoteType,
        ...(timedStart ? [timedStart.startAudioTime] : []),
      );
      this.lastScheduledBeat = -10000;
      this.scheduleMetronomeAudio();
      this.tickIntervalFn = this._startTickIntervalLoop();

      for (const unpauseCb of this.unpauseFns) {
        unpauseCb(
          this.timeKeeper?.calculateElapsedTicks(),
          this.timeKeeper?.getPlaybackStartTicks(),
          this.timeKeeper?.getTicksPerMeasure(),
        );
      }

      this.handlingPauseOrUnpause = false;
    }
  };
}
